import AWS from 'aws-sdk'
import { Role, SignalingClient } from 'amazon-kinesis-video-streams-webrtc'

/**
 * This file demonstrates the process of starting WebRTC streaming using a KVS Signaling Channel.
 */
const master = {
  signalingClient: null,
  peerConnectionByClientId: {},
  dataChannelByClientId: {},
  localStream: null,
  remoteStreams: [],
  peerConnectionStatsInterval: null
}

async function startMaster(
  localView,
  formValues,
  onStatsReport,
  onRemoteDataMessage
) {
  master.localView = localView

  // Create KVS client
  const kinesisVideoClient = new AWS.KinesisVideo({
    endpoint: formValues.endpoint,
    correctClockSkew: true
  })

  // Get signaling channel ARN
  const describeSignalingChannelResponse = await kinesisVideoClient
    .describeSignalingChannel({
      ChannelName: formValues.channelName
    })
    .promise()
  const channelARN = describeSignalingChannelResponse.ChannelInfo.ChannelARN
  console.log('[MASTER] Channel ARN: ', channelARN)

  // Get signaling channel endpoints
  const getSignalingChannelEndpointResponse = await kinesisVideoClient
    .getSignalingChannelEndpoint({
      ChannelARN: channelARN,
      SingleMasterChannelEndpointConfiguration: {
        Protocols: ['WSS', 'HTTPS'],
        Role: Role.MASTER
      }
    })
    .promise()
  const endpointsByProtocol =
    getSignalingChannelEndpointResponse.ResourceEndpointList.reduce(
      (endpoints, endpoint) => {
        endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint
        return endpoints
      },
      {}
    )
  console.log('[MASTER] Endpoints: ', endpointsByProtocol)

  // Create Signaling Client
  master.signalingClient = new SignalingClient({
    channelARN,
    channelEndpoint: endpointsByProtocol.WSS,
    role: Role.MASTER,
    region: AWS.config.region,
    credentials: AWS.config.credentials,
    systemClockOffset: kinesisVideoClient.config.systemClockOffset
  })

  // Get ICE server configuration
  const kinesisVideoSignalingChannelsClient =
    new AWS.KinesisVideoSignalingChannels({
      region: AWS.config.region,
      endpoint: endpointsByProtocol.HTTPS,
      correctClockSkew: true
    })
  const getIceServerConfigResponse = await kinesisVideoSignalingChannelsClient
    .getIceServerConfig({
      ChannelARN: channelARN
    })
    .promise()
  const iceServers = []
  if (!formValues.natTraversalDisabled && !formValues.forceTURN) {
    iceServers.push({
      urls: `stun:stun.kinesisvideo.${formValues.region}.amazonaws.com:443`
    })
  }
  if (!formValues.natTraversalDisabled) {
    getIceServerConfigResponse.IceServerList.forEach((iceServer) =>
      iceServers.push({
        urls: iceServer.Uris,
        username: iceServer.Username,
        credential: iceServer.Password
      })
    )
  }
  console.log('[MASTER] ICE servers: ', iceServers)

  const configuration = {
    iceServers,
    iceTransportPolicy: formValues.forceTURN ? 'relay' : 'all'
  }

  // const getVideoResolution = () => {
  //   return {
  //     width: { min: 1280, ideal: 1280, max: 1280 },
  //     height: { min: 720, ideal: 720, max: 720 }
  //   }
  //   // const isMobileDevice = /Mobile/.test(navigator.userAgent)
  //   // if (isMobileDevice) {
  //   //   const availableResolutions = {
  //   //     '360p': { width: 640, height: 360 },
  //   //     '480p': { width: 854, height: 480 },
  //   //     '720p': { width: 1280, height: 720 }
  //   //   }

  //   //   let selectedResolution = '360p'
  //   //   const connectionSpeed = navigator.connection
  //   //     ? navigator.connection.effectiveType
  //   //     : '4g'
  //   //   console.log(`Connection Speed: ${connectionSpeed}`)
  //   //   const devicePixelRatio = window.devicePixelRatio || 1
  //   //   const screenWidth = screen.width * devicePixelRatio
  //   //   const screenHeight = screen.height * devicePixelRatio

  //   //   if (
  //   //     connectionSpeed === '4g' &&
  //   //     screenWidth >= 854 &&
  //   //     screenHeight >= 480
  //   //   ) {
  //   //     selectedResolution = '480p'
  //   //   } else if (
  //   //     connectionSpeed === '4g' &&
  //   //     screenWidth >= 640 &&
  //   //     screenHeight >= 360
  //   //   ) {
  //   //     selectedResolution = '360p'
  //   //   } else if (
  //   //     connectionSpeed === '3g' &&
  //   //     screenWidth >= 640 &&
  //   //     screenHeight >= 360
  //   //   ) {
  //   //     selectedResolution = '240p'
  //   //   } else {
  //   //     selectedResolution = '180p'
  //   //   }

  //   //   const res = availableResolutions[selectedResolution]
  //   //   console.log(`Resolution: ${res.width} x ${res.height}`)
  //   //   return res
  //   // } else {
  //   //   return formValues.widescreen
  //   //     ? { width: { exact: 1280 }, height: { exact: 720 } }
  //   //     : { width: { exact: 640 }, height: { exact: 480 } }
  //   // }
  // }

  const videoConstraints = {
    width: { min: 1280, ideal: 1280, max: 1280 },
    height: { min: 720, ideal: 720, max: 720 },
    frameRate: { ideal: 25, max: 25 }
  }

  const constraints = {
    video: formValues.sendVideo && videoConstraints,
    audio: formValues.sendAudio
  }

  // Get a stream from the webcam and display it in the local view.
  // If no video/audio needed, no need to request for the sources.
  // Otherwise, the browser will throw an error saying that either video or audio has to be enabled.
  if (formValues.sendVideo || formValues.sendAudio) {
    try {
      master.localStream = await navigator.mediaDevices.getUserMedia(
        constraints
      )
      localView.srcObject = master.localStream
    } catch (e) {
      console.error('[MASTER] Could not find webcam')
    }
  }

  master.signalingClient.on('open', async () => {
    console.log('[MASTER] Connected to signaling service')
  })

  master.signalingClient.on('sdpOffer', async (offer, remoteClientId) => {
    console.log('[MASTER] Received SDP offer from client: ' + remoteClientId)

    // Create a new peer connection using the offer from the given client
    // eslint-disable-next-line no-undef
    const peerConnection = new RTCPeerConnection(configuration)
    master.peerConnectionByClientId[remoteClientId] = peerConnection

    if (formValues.openDataChannel) {
      master.dataChannelByClientId[remoteClientId] =
        peerConnection.createDataChannel('kvsDataChannel')
      peerConnection.ondatachannel = (event) => {
        event.channel.onmessage = onRemoteDataMessage
      }
    }

    // Poll for connection stats
    if (!master.peerConnectionStatsInterval) {
      master.peerConnectionStatsInterval = setInterval(
        () => peerConnection.getStats().then(onStatsReport),
        1000
      )
    }

    // Send any ICE candidates to the other peer
    peerConnection.addEventListener('icecandidate', ({ candidate }) => {
      if (candidate) {
        console.log(
          '[MASTER] Generated ICE candidate for client: ' + remoteClientId
        )

        // When trickle ICE is enabled, send the ICE candidates as they are generated.
        if (formValues.useTrickleICE) {
          console.log(
            '[MASTER] Sending ICE candidate to client: ' + remoteClientId
          )
          master.signalingClient.sendIceCandidate(candidate, remoteClientId)
        }
      } else {
        console.log(
          '[MASTER] All ICE candidates have been generated for client: ' +
            remoteClientId
        )

        // When trickle ICE is disabled, send the answer now that all the ICE candidates have ben generated.
        if (!formValues.useTrickleICE) {
          console.log(
            '[MASTER] Sending SDP answer to client: ' + remoteClientId
          )
          master.signalingClient.sendSdpAnswer(
            peerConnection.localDescription,
            remoteClientId
          )
        }
      }
    })

    // If there's no video/audio, master.localStream will be null. So, we should skip adding the tracks from it.
    if (master.localStream) {
      master.localStream.getTracks().forEach((track) => {
        const sender = peerConnection.addTrack(track, master.localStream)
        const params = sender.getParameters()
        console.log('sender params', params)

        if (!params.encodings) {
          params.encodings = [{}]
        }
        // set fixed frame rate
        params.encodings[0].maxFramerate = 25        

        sender
          .setParameters(params)
          .then((params) => {
            const p = sender.getParameters()
            console.log('Frame rate set successfully', p)
          })
          .catch((error) => {
            console.error('Error setting frame rate:', error)
          })
      })
    }

    await peerConnection.setRemoteDescription(offer)

    // Create an SDP answer to send back to the client
    console.log('[MASTER] Creating SDP answer for client: ' + remoteClientId)
    await peerConnection.setLocalDescription(
      await peerConnection.createAnswer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      })
    )

    // When trickle ICE is enabled, send the answer now and then send ICE candidates as they are generated. Otherwise wait on the ICE candidates.
    if (formValues.useTrickleICE) {
      console.log('[MASTER] Sending SDP answer to client: ' + remoteClientId)
      master.signalingClient.sendSdpAnswer(
        peerConnection.localDescription,
        remoteClientId
      )
    }
    console.log(
      '[MASTER] Generating ICE candidates for client: ' + remoteClientId
    )
  })

  master.signalingClient.on(
    'iceCandidate',
    async (candidate, remoteClientId) => {
      console.log(
        '[MASTER] Received ICE candidate from client: ' + remoteClientId
      )
      try {
        // Add the ICE candidate received from the client to the peer connection
        const peerConnection = master.peerConnectionByClientId[remoteClientId]
        peerConnection.addIceCandidate(candidate)
      } catch (e) {
        console.log(e)
      }
    }
  )

  master.signalingClient.on('close', () => {
    console.log('[MASTER] Disconnected from signaling channel')
  })

  master.signalingClient.on('error', (err) => {
    console.error('[MASTER] Signaling client error', err)
  })

  console.log('[MASTER] Starting master connection')
  master.signalingClient.open()
}

export function handleMasterVideo(state) {
  if (state) {
    master.localStream.getTracks().forEach(function (track) {
      if (track.readyState === 'live' && track.kind === 'video') {
        track.enabled = false
      }
    })
  } else {
    master.localStream.getTracks().forEach(function (track) {
      if (track.readyState === 'live' && track.kind === 'video') {
        track.enabled = true
      }
    })
  }
}

export function handleMasterAudio(state) {
  if (state) {
    master.localStream.getTracks().forEach(function (track) {
      if (track.readyState === 'live' && track.kind === 'audio') {
        track.enabled = false
      }
    })
  } else {
    master.localStream.getTracks().forEach(function (track) {
      if (track.readyState === 'live' && track.kind === 'audio') {
        track.enabled = true
      }
    })
  }
}

export function stopMaster() {
  console.log('[MASTER] Stopping master connection')
  if (master.signalingClient) {
    master.signalingClient.close()
    master.signalingClient = null
  }

  Object.keys(master.peerConnectionByClientId).forEach((clientId) => {
    master.peerConnectionByClientId[clientId].close()
  })
  master.peerConnectionByClientId = []

  if (master.localStream) {
    master.localStream.getTracks().forEach((track) => track.stop())
    master.localStream = null
  }

  master.remoteStreams.forEach((remoteStream) =>
    remoteStream.getTracks().forEach((track) => track.stop())
  )
  master.remoteStreams = []

  if (master.peerConnectionStatsInterval) {
    clearInterval(master.peerConnectionStatsInterval)
    master.peerConnectionStatsInterval = null
  }

  if (master.localView) {
    master.localView.srcObject = null
  }

  if (master.dataChannelByClientId) {
    master.dataChannelByClientId = {}
  }
}

async function getMasterCredentials(
  localView,
  formValues,
  onStatsReport,
  onRemoteDataMessage,
  cognito,
  identity
) {
  const params = {
    IdentityId: identity
  }

  cognito.getOpenIdToken(params, function (err, data) {
    if (err) console.log(err, err.stack)
    else {
      AWS.config.credentials.params.WebIdentityToken = data.Token
      startMaster(localView, formValues, onStatsReport, onRemoteDataMessage)
    }
  })
}

export async function getMasterIdentity(
  localView,
  formValues,
  region,
  identityPoolId,
  roleARN,
  onStatsReport,
  onRemoteDataMessage
) {
  AWS.config = {
    region: region,
    IdentityPoolId: identityPoolId,
    credentials: new AWS.WebIdentityCredentials({
      RoleArn: roleARN
    })
  }

  const cognito = new AWS.CognitoIdentity()
  const params = {
    IdentityPoolId: identityPoolId
  }
  cognito.getId(params, function (err, data) {
    if (err) console.log(err, err.stack)
    else
      getMasterCredentials(
        localView,
        formValues,
        onStatsReport,
        onRemoteDataMessage,
        cognito,
        data.IdentityId
      )
  })
}

export function sendMasterMessage(message) {
  Object.keys(master.dataChannelByClientId).forEach((clientId) => {
    try {
      console.log(`sendMasterMessage: clientId - ${clientId}`)
      master.dataChannelByClientId[clientId].send(message)
    } catch (e) {
      console.error('[MASTER] Send DataChannel: ', e.toString())
    }
  })
}
